import { _ } from "../../../../core/polyglot/polyglot.js";
import { month } from "../../../../models/period/PeriodType.js";
import { select } from "../../../../core/elements/index.js";
import AccountingDocumentWidget from "../../../shared/documentInfrastructure/AccountingDocumentWidget.js";
import commandHeadingWidget from "../../../document/widgets/commandHeadingWidget.js";
import ExcelExporter from "../../../reports2/export/ExcelExporter.js";
import IndexPicker from "../../../shared/IndexPicker/IndexPicker.js";
import makeReportExporterPicker from "../../../reports2/export/makeReportExporterPicker.js";
import PeriodTypePicker from "../../../shared/PeriodTypePicker/PeriodTypePicker.js";
import popoverActionButtonWidget from "../../../../core/widgets/popoverActionButtonWidget.js";
import SubColumnSpecListWidget from "../../subColumnSpec/SubColumnSpecListWidget.js";

/**
 * Widget that holds all the actions that can be done in the performance
 * comparison document. These actions will be rendered in the right-hand
 * menu.
 *
 * @param {Company} [company] - The company owning the report
 * @param {Period} [period] - The period of the report
 */
export default class ComparisonToolDocumentActions extends AccountingDocumentWidget {
	constructor({ company, period }) {
		super(...arguments);

		this._company = company;
		this._period = period;
	}

	setup(...args) {
		super.setup(...args);
		this.update();
	}

	renderContentOn(html) {
		if (!this._documentState) return;

		html.div(
			{
				class: "ComparisonToolDocumentActions",
				"data-test": "comparison-tool-actions",
			},
			this._renderReportTemplateRowsSelectOn.bind(this),
			this._renderPeriodColumnsSectionOn.bind(this),
			this._renderSubColumnsSectionOn.bind(this),
			this._renderSummaryColumnSectionOn.bind(this),
			this._renderExporterSectionOn.bind(this),
		);
	}

	_renderReportTemplateRowsSelectOn(html) {
		return html.div(
			{ class: "right-menu-section" },
			select({
				items: this._documentState.getReportTemplateItems(),
				onChange: (reportTemplate) =>
					this._documentState.setReportTemplate(reportTemplate),
				ref: (ref) =>
					ref.setAttribute("name", "select-report-template-rows"),
			}),
		);
	}

	_renderPeriodColumnsSectionOn(html) {
		const pastRepeatingColumnTemplate =
			this._documentState.getPastRepeatingColumnTemplate();

		const futureRepeatingColumnTemplate =
			this._documentState.getFutureRepeatingColumnTemplate();

		html.render(
			commandHeadingWidget({
				name: _("Periods"),
				commandWidgets: [
					this._numberOfPeriodsRenderer({
						repeatingColumnTemplate: pastRepeatingColumnTemplate,
						dataTest: "number-of-past-periods",
						label: _("Number of past periods"),
						max: this._period.getPeriodIndex(),
					}),
					this._numberOfPeriodsRenderer({
						repeatingColumnTemplate: futureRepeatingColumnTemplate,
						dataTest: "number-of-future-periods",
						label: _("Number of future periods"),
						min: 0,
					}),
					this._periodTypePickerRenderer({
						periodType: pastRepeatingColumnTemplate.getPeriodType(),
					}),
				],
			}),
		);
	}

	_numberOfPeriodsRenderer({
		repeatingColumnTemplate,
		dataTest,
		label,
		min = 1,
		max,
	}) {
		return (html) =>
			html.div(
				{ "data-test": dataTest },
				html.span({ class: "switch-label" }, label),
				new IndexPicker({
					min,
					index: repeatingColumnTemplate.getNumberOfPeriods(),
					max,
					callback: (value) => {
						repeatingColumnTemplate.setNumberOfPeriods(value);
						this._documentState.updateReport();
					},
				}),
			);
	}

	_periodTypePickerRenderer({ periodType }) {
		return (html) =>
			html.div(
				{ "data-test": "period-type" },
				html.span({ class: "switch-label" }, _("Period type")),
				PeriodTypePicker.forCommonTypes({
					isBimesterEnabled: this._documentState.isBimesterEnabled(),
					isYearToDateEnabled:
						this._documentState.isYearToDateEnabled(),
					spec: {
						minimumPeriodType: month,
						selectedPeriodTypeId: periodType,
						onChange: async (selectedPeriodType) => {
							await this._documentState.setPeriodType(
								selectedPeriodType.getId(),
							);

							this.update();
						},
					},
				}),
			);
	}

	_renderSubColumnsSectionOn(html) {
		const label = _("Sub-columns");

		this._subColumnPopover = popoverActionButtonWidget({
			label,
			popoverHeader: label,
			popoverContent: this._makeSubColumnSpecListWidget(),
			popoverPrimaryButtonLabel: null,
			popoverCssClass:
				"popover filter-content-picker-popover popover-report-settings comparison-tool",
		});

		html.div({ class: "right-menu-section" }, this._subColumnPopover);
	}

	_makeSubColumnSpecListWidget() {
		return new SubColumnSpecListWidget({
			subColumnSpecs: this._documentState.getSubColumnSpecs(),
			forecastEstimates: this._documentState.getForecastEstimates(),
			budgets: this._documentState.getBudgets(),
			onChange: (specs) => {
				this._documentState.setSubColumnSpecs(specs);
				this._subColumnPopover.hide();
			},
		});
	}

	_renderSummaryColumnSectionOn(html) {
		const periodTypes = this._documentState.getLargerPeriodTypes();

		if (!periodTypes.length) return;

		html.div(
			{ class: "right-menu-section" },
			periodTypes.map((periodType) =>
				this._makeSummaryPopoverButton({ periodType }),
			),
		);
	}

	_makeSummaryPopoverButton({ periodType }) {
		const label = periodType.getName();

		return popoverActionButtonWidget({
			label,
			dataTestName: "summary",
			popoverHeader: _("Select sub-columns for {type}", { type: label }),
			popoverContent: this._makeSummarySpecListWidget({ periodType }),
			popoverPrimaryButtonLabel: null,
			popoverCssClass:
				"popover filter-content-picker-popover popover-report-settings comparison-tool",
		});
	}

	_makeSummarySpecListWidget({ periodType }) {
		return new SubColumnSpecListWidget({
			subColumnSpecs: this._documentState.getSummarySpecs({ periodType }),
			forecastEstimates: this._documentState.getForecastEstimates(),
			budgets: this._documentState.getBudgets(),
			canEditActuals: true,
			onChange: (specs) =>
				this._documentState.setSummarySpecs({ specs, periodType }),
		});
	}

	_renderExporterSectionOn(html) {
		html.div(
			{ class: "right-menu-section" },
			html.label(_("Export")),
			html.div(
				{ class: "right-menu-block" },
				makeReportExporterPicker({
					report: this._documentState.getReport(),
					company: this._company,
					exporters: [new ExcelExporter()],
				}),
			),
		);
	}
}
